import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Link2,
  Unlink,
} from "lucide-react";

interface NoteEditorProps {
  content: string;
  onUpdate?: (html: string) => void;
  editable?: boolean;
  actions?: ReactNode;
}

function EditorToolbar({ editor, actions }: { editor: ReturnType<typeof useEditor>; actions?: ReactNode }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  if (!editor) return null;

  const isLinkActive = editor.isActive("link");

  const handleLinkButtonClick = () => {
    setLinkUrl(editor.getAttributes("link").href ?? "");
    setLinkOpen(true);
  };

  const handleSetLink = () => {
    const trimmed = linkUrl.trim();
    if (!trimmed) {
      editor.chain().focus().unsetLink().run();
    } else {
      const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      editor.chain().focus().setLink({ href }).run();
    }
    setLinkOpen(false);
  };

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setLinkOpen(false);
  };

  return (
    <div className="flex items-center gap-0.5 border-b px-2 py-1" data-testid="note-editor-toolbar">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`toggle-elevate ${editor.isActive("bold") ? "toggle-elevated" : ""}`}
        data-testid="button-note-bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`toggle-elevate ${editor.isActive("italic") ? "toggle-elevated" : ""}`}
        data-testid="button-note-italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`toggle-elevate ${editor.isActive("heading", { level: 2 }) ? "toggle-elevated" : ""}`}
        data-testid="button-note-heading2"
      >
        <Heading2 className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`toggle-elevate ${editor.isActive("heading", { level: 3 }) ? "toggle-elevated" : ""}`}
        data-testid="button-note-heading3"
      >
        <Heading3 className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`toggle-elevate ${editor.isActive("bulletList") ? "toggle-elevated" : ""}`}
        data-testid="button-note-bullet-list"
      >
        <List className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`toggle-elevate ${editor.isActive("orderedList") ? "toggle-elevated" : ""}`}
        data-testid="button-note-ordered-list"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </Button>

      <Popover open={linkOpen} onOpenChange={setLinkOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLinkButtonClick}
            className={`toggle-elevate ${isLinkActive ? "toggle-elevated" : ""}`}
            data-testid="button-note-link"
            aria-label="Insert link"
          >
            <Link2 className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="flex flex-col gap-2">
            <Input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); handleSetLink(); }
                if (e.key === "Escape") setLinkOpen(false);
              }}
              data-testid="input-link-url"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSetLink} className="flex-1" data-testid="button-link-set">
                {isLinkActive ? "Update" : "Set Link"}
              </Button>
              {isLinkActive && (
                <Button size="icon" variant="ghost" onClick={handleRemoveLink} aria-label="Remove link" data-testid="button-link-remove">
                  <Unlink className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {actions && <div className="ml-auto flex items-center gap-1">{actions}</div>}
    </div>
  );
}

export function NoteEditor({ content, onUpdate, editable = true, actions }: NoteEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content,
    editable,
    onUpdate: ({ editor: e }) => {
      onUpdate?.(e.getHTML());
    },
  });

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  useEffect(() => {
    if (editor && !editor.isDestroyed && content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  return (
    <div className="border rounded-md overflow-hidden flex flex-col h-full" data-testid="note-editor-container">
      {editable && <EditorToolbar editor={editor} actions={actions} />}
      <div
        className="flex-1 min-h-[120px] overflow-y-auto pt-[5px] px-3 pb-3 prose prose-sm dark:prose-invert max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground prose-a:text-foreground prose-blockquote:text-foreground [&_.tiptap]:outline-none [&_.tiptap]:min-h-[100px] [&_.tiptap_.is-empty]:text-muted-foreground [&_.tiptap>*:first-child]:mt-0 [&_.tiptap>*:last-child]:mb-0"
        data-testid="note-editor-content"
        onClick={editable ? (e) => { if (e.target === e.currentTarget) editor?.commands.focus("end"); } : undefined}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
